<template>
  <main class="wallet-withdraw-page">
    <AppNavBar :title="t('home.withdraw')" />

    <template v-if="coinList.length || withdrawStore.loading">
      <section
        v-if="coinList.length"
        class="wallet-card selector-card"
        @click="goSelectCoin"
      >
        <div class="coin-title">
          <app-image :src="selectedCoin.logo || usdtIcon" alt="" />
          <div data-strong>{{ selectedCoin.symbol || "--" }}</div>
        </div>
        <span>
          {{ t("wallet.chooseCoin") }}
          <i class="selector-card__arrow" aria-hidden="true" />
        </span>
      </section>

      <section v-if="chains.length" class="wallet-card network-card">
        <h2>{{ t("wallet.chooseNetwork") }}</h2>
        <div class="network-select" @click.stop="toggleChainPopup">
          <span>{{ selectedChainLabel }}</span>
          <van-icon name="arrow-down" />
        </div>
        <div v-if="showChainPopup" class="network-options">
          <div
            v-for="option in chainOptions"
            :key="`${option.text}-${option.value}`"
            :class="[
              'network-options__item',
              {
                'network-options__item--active':
                  selectedChainIndex === option.value,
              },
            ]"
            @click.stop="selectChain(option.value)"
          >
            {{ option.text }}
          </div>
        </div>
      </section>
      <div
        v-if="showChainPopup"
        class="network-mask"
        @click="showChainPopup = false"
      />

      <section class="wallet-card input-card">
        <h2>{{ t("wallet.withdrawAddress") }}</h2>
        <div class="input-shell">
          <input
            v-model="addressTo"
            type="text"
            :placeholder="t('wallet.enterWithdrawAddress')"
          />
          <button type="button" class="scan-btn" @click="openScanner">
            <app-image src="@/assets/images/scancode_icon3.png" alt="" />
          </button>
        </div>
      </section>

      <section v-if="coinList.length" class="wallet-card">
        <div class="amount-title">
          <h2>{{ t("wallet.withdrawAmountLabel") }}</h2>
          <span
            >{{ t("wallet.available") }}
            {{ selectedCoin.balanceNormal || selectedCoin.balance || "0" }}
            {{ selectedCoin.symbol }}</span
          >
        </div>

        <div class="amount-field">
          <input
            v-model="amount"
            type="number"
            inputmode="decimal"
            :placeholder="t('wallet.enterWithdrawAmount')"
          />
          <div class="amount-side">
            <span class="amount-symbol">{{ selectedCoin.symbol }}</span>
            <button class="all-btn" type="button" @click="fillAll">
              {{ t("wallet.all") }}
            </button>
          </div>
        </div>

        <div class="dashed-divider" />
        <div class="detail-row">
          <span>{{ t("wallet.fee") }}</span>
          <div data-strong>{{ feeRateText }}</div>
        </div>
        <div class="dashed-divider" />
        <div class="detail-row">
          <span>{{ t("wallet.received") }}</span>
          <div data-strong>{{ receivedAmount }} {{ selectedCoin.symbol }}</div>
        </div>
      </section>

      <van-button
        block
        round
        type="primary"
        class="submit-btn"
        @click="confirmWithdraw"
      >
        {{ t("wallet.submitWithdraw") }}
      </van-button>

      <PayPasswordPopup
        v-model:show="showPayPassword"
        v-model:password="payPassword"
        :title="t('auth.verifyWalletPassword')"
        :placeholder="t('auth.enterPassword')"
        :confirm-text="t('common.confirm')"
        :forgot-text="t('auth.forgotPasswordRecovery')"
        :show-forgot="true"
        :confirm-disabled="
          !payPassword.trim() || isSubmitting || isCheckingPassword
        "
        @confirm="confirmPassword"
        @cancel="resetPasswordPopup"
        @closed="resetPasswordPopup"
        @forgot="goForgotPassword"
      />

      <van-popup
        :show="showWithdrawVerification"
        position="bottom"
        class="withdraw-email-verification-popup"
        :close-on-click-overlay="false"
        :overlay-style="{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }"
        @update:show="handleWithdrawVerificationShowUpdate"
        @closed="resetWithdrawVerification"
      >
        <section class="withdraw-email-verification">
          <header class="withdraw-email-verification__head">
            <h2>{{ t("wallet.transferIdentityVerification") }}</h2>
            <button type="button" @click="closeWithdrawVerification">
              <img :src="withdrawDialogCloseIcon" alt="" />
            </button>
          </header>

          <p class="withdraw-email-verification__desc">
            {{ t("wallet.withdrawEmailVerificationDesc") }}
          </p>

          <div class="withdraw-email-verification__account">
            <span class="withdraw-email-verification__image">
              <img :src="withdrawEmailVerifyImage" alt="" />
            </span>
            <span class="withdraw-email-verification__email">
              <strong>{{ t("wallet.transferVerifyEmail") }}</strong>
              <span>{{ withdrawEmail || "--" }}</span>
            </span>
          </div>

          <div class="withdraw-email-verification__code">
            <label>{{ t("auth.emailCode") }}</label>
            <van-field
              v-model="emailCode"
              type="digit"
              maxlength="8"
              :placeholder="t('auth.enterVerificationCode')"
            >
              <template #button>
                <button
                  type="button"
                  class="withdraw-email-verification__send"
                  :disabled="
                    isSendingEmailCode || emailCountdown > 0 || !withdrawEmail
                  "
                  @click="sendWithdrawEmailCode"
                >
                  {{
                    emailCountdown > 0
                      ? `${t("auth.resend")}${emailCountdown}s`
                      : t("auth.getCode")
                  }}
                </button>
              </template>
            </van-field>
            <p>{{ t("wallet.transferEmailCodeExpires") }}</p>
          </div>

          <div class="withdraw-email-verification__footer">
            <van-button
              block
              round
              type="primary"
              :disabled="!emailCode.trim() || isSubmitting"
              @click="verifyWithdraw"
            >
              {{ t("auth.verify") }}
            </van-button>
            <span class="withdraw-email-verification__home-indicator" />
          </div>
        </section>
      </van-popup>

      <section class="wallet-card notes-card">
        <h2>{{ t("wallet.withdrawNotes") }}</h2>
        <p>
          {{ t("wallet.withdrawNote1Prefix") }}
          <span data-strong>{{ withdrawMinText }}</span>
        </p>
        <p>{{ t("wallet.withdrawNote2") }}</p>
        <p>
          {{ t("wallet.withdrawNote3Prefix") }}
          <span data-strong>0-2</span>
          {{ t("wallet.withdrawNote3Suffix") }}
        </p>
        <p>{{ t("wallet.withdrawNote4") }}</p>
      </section>
    </template>

    <section v-else class="empty-wrap">
      <app-image src="@/assets/images/emptydata_gray.png" alt="" />
      <span>{{ t("wallet.withdrawUnavailable") }}</span>
    </section>
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { closeToast, showFailToast, showLoadingToast } from "vant";
import PayPasswordPopup from "@/components/PayPasswordPopup.vue";
import { devicesInfo, emailSend, payPwdCheck } from "@/api/auth";
import { showPassedToast } from "@/utils/toast";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { pcCardAssetWithdraw } from "@/api/wallet";
import { useWalletScanner } from "@/composables/useWalletScanner";
import { normalizeFeeRate } from "@/view/card/utils";
import { getDeviceId } from "@/utils/device";
import { useProfileStore } from "@/stores/profile";
import { useWalletWithdrawStore } from "@/stores/wallet-withdraw";
import { formatTokenAmount } from "@/utils/format";
import { getRouteState, navigateWithState } from "@/utils/route-state";
import withdrawDialogCloseIcon from "@/assets/images/cancel_gray.png";
import withdrawEmailVerifyImage from "@/assets/images/transfer-email-verify.png";
import usdtIcon from "@/assets/images/usdt.png";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { openWalletScanner } = useWalletScanner();
const profileStore = useProfileStore();
const withdrawStore = useWalletWithdrawStore();
const pageState = computed(() => getRouteState(route, "wallet-withdraw", {}));

const selectedChainIndex = ref(0);
const showChainPopup = ref(false);
const addressTo = ref("");
const amount = ref("");
const showPayPassword = ref(false);
const payPassword = ref("");
const pendingPayPassword = ref("");
const showWithdrawVerification = ref(false);
const emailCode = ref("");
const withdrawEmail = ref(
  String(profileStore.currentAccount?.email || "").trim(),
);
const emailCountdown = ref(0);
const isSendingEmailCode = ref(false);
const isCheckingPassword = ref(false);
const isSubmitting = ref(false);
let emailCountdownTimer = null;

const coinList = computed(() => withdrawStore.list);
const selectedCoin = computed(
  () =>
    withdrawStore.selectedCoin || {
      symbol: "USDT",
      logo: "",
      balanceNormal: "0",
      chains: [
        {
          symbolType: "BEP20",
          feeRate: "0.01",
          feeMin: "1",
        },
      ],
    },
);
const chains = computed(() => selectedCoin.value.chains || []);
const selectedChain = computed(
  () => chains.value[selectedChainIndex.value] || {},
);
const minAmount = computed(() =>
  Number(selectedChain.value.amountMin || selectedCoin.value.amountMin || 0),
);
const maxAmount = computed(() =>
  Number(selectedChain.value.amountMax || selectedCoin.value.amountMax || 0),
);
const chainOptions = computed(() =>
  chains.value.map((item, index) => ({
    text: item.symbolType || item.chainName || `Network ${index + 1}`,
    value: index,
  })),
);
const selectedChainLabel = computed(
  () => chainOptions.value[selectedChainIndex.value]?.text || "--",
);
const withdrawMinText = computed(() =>
  `${formatTokenAmount(minAmount.value || 0)} ${selectedCoin.value.symbol || ""}`.trim(),
);

const feeValue = computed(() => {
  const value = Number(amount.value || 0);
  const rate = normalizeFeeRate(selectedChain.value.feeRate);
  if (!value) {
    return 0;
  }
  return Math.max(value * rate);
});
const receivedAmount = computed(() =>
  formatTokenAmount(Math.max(Number(amount.value || 0) - feeValue.value, 0)),
);
const feeRateText = computed(
  () =>
    `${formatTokenAmount(normalizeFeeRate(selectedChain.value.feeRate) * 100)}%`,
);

watch(
  () => withdrawStore.selectedCoinIndex,
  () => {
    selectedChainIndex.value = 0;
    amount.value = "";
    showChainPopup.value = false;
  },
);

watch(
  () => pageState.value.scanResult,
  (value) => {
    if (!value) {
      return;
    }
    addressTo.value = String(value).split("&")[0];
    navigateWithState(router, route.path, {
      namespace: "wallet-withdraw",
      replace: true,
      payload: {
        ...pageState.value,
        scanResult: "",
      },
    });
  },
  { immediate: true },
);

function toggleChainPopup() {
  if (chainOptions.value.length <= 1) {
    return;
  }
  showChainPopup.value = !showChainPopup.value;
}

function selectChain(index) {
  selectedChainIndex.value = index;
  showChainPopup.value = false;
}

function goSelectCoin() {
  router.push("/wallet/withdraw/select-coin");
}

function openScanner() {
  openWalletScanner({
    back: "/wallet/withdraw",
    stateNamespace: "wallet-withdraw",
    statePayload: {
      ...pageState.value,
    },
  });
}

function fillAll() {
  amount.value = String(
    selectedCoin.value.balanceNormal || selectedCoin.value.balance || "0",
  );
}

async function confirmWithdraw() {
  const amountValue = Number(amount.value || 0);

  if (!addressTo.value.trim()) {
    showFailToast(t("wallet.enterWithdrawAddress"));
    return;
  }

  if (!Number.isFinite(amountValue) || amountValue <= 0) {
    showFailToast(t("card.enterValidAmount"));
    return;
  }

  if (minAmount.value && amountValue < minAmount.value) {
    showFailToast(
      `${t("card.minAmount")} ${formatTokenAmount(minAmount.value)} ${selectedCoin.value.symbol}`,
    );
    return;
  }

  if (maxAmount.value && amountValue > maxAmount.value) {
    showFailToast(
      `${t("wallet.maxAmountLabel")} ${formatTokenAmount(maxAmount.value)} ${selectedCoin.value.symbol}`,
    );
    return;
  }

  emailCode.value = "";
  pendingPayPassword.value = "";
  showPayPassword.value = true;
}

function resetPasswordPopup() {
  payPassword.value = "";
  showPayPassword.value = false;
  if (!showWithdrawVerification.value) {
    pendingPayPassword.value = "";
  }
}

async function confirmPassword() {
  if (isCheckingPassword.value) {
    return;
  }

  const payPwd = String(payPassword.value || "").trim();
  if (!payPwd) {
    showFailToast(t("auth.enterPassword"));
    return;
  }

  isCheckingPassword.value = true;
  try {
    const isValid = await payPwdCheck({ payPwd });
    if (!isValid) {
      showFailToast(t("auth.passwordError"));
      return;
    }

    pendingPayPassword.value = payPwd;
    showWithdrawVerification.value = true;
    showPayPassword.value = false;
    void loadWithdrawEmail();
  } catch (error) {
    showFailToast(error?.msg || error?.message || t("common.retryLater"));
  } finally {
    isCheckingPassword.value = false;
  }
}

async function loadWithdrawEmail() {
  const currentEmail = String(profileStore.currentAccount?.email || "").trim();
  if (currentEmail) {
    withdrawEmail.value = currentEmail;
    return currentEmail;
  }

  try {
    const result = await devicesInfo(
      { token: getDeviceId() },
      { meta: { showLoading: false } },
    );
    const loadedEmail = String(result?.email || "").trim();
    if (loadedEmail) {
      withdrawEmail.value = loadedEmail;
      profileStore.setCurrentAccount({
        ...profileStore.currentAccount,
        email: loadedEmail,
      });
    }
    return loadedEmail;
  } catch {
    return "";
  }
}

function startEmailCountdown() {
  emailCountdown.value = 60;
  window.clearInterval(emailCountdownTimer);
  emailCountdownTimer = window.setInterval(() => {
    emailCountdown.value -= 1;
    if (emailCountdown.value <= 0) {
      window.clearInterval(emailCountdownTimer);
      emailCountdownTimer = null;
    }
  }, 1000);
}

async function sendWithdrawEmailCode() {
  if (isSendingEmailCode.value || emailCountdown.value > 0) {
    return;
  }

  isSendingEmailCode.value = true;
  try {
    const email = await loadWithdrawEmail();
    if (!email) {
      showFailToast(t("auth.bindEmailTip"));
      return;
    }
    await emailSend({
      token: getDeviceId(),
      email,
      type: "WITHDRAW",
      label: "ZORPAY",
    });
    startEmailCountdown();
    showPassedToast(t("auth.codeSent"));
  } catch (error) {
    showFailToast(error?.msg || error?.message || t("common.retryLater"));
  } finally {
    isSendingEmailCode.value = false;
  }
}

function closeWithdrawVerification() {
  showWithdrawVerification.value = false;
}

function handleWithdrawVerificationShowUpdate(value) {
  showWithdrawVerification.value = value;
}

function resetWithdrawVerification() {
  emailCode.value = "";
  pendingPayPassword.value = "";
  payPassword.value = "";
  if (emailCountdown.value <= 0) {
    withdrawEmail.value = String(
      profileStore.currentAccount?.email || "",
    ).trim();
  }
}

async function verifyWithdraw() {
  if (isSubmitting.value) {
    return;
  }
  if (!pendingPayPassword.value) {
    showFailToast(t("auth.enterPassword"));
    return;
  }
  if (!emailCode.value.trim()) {
    showFailToast(t("auth.codeRequired"));
    return;
  }

  isSubmitting.value = true;
  showLoadingToast({
    message: t("common.loading"),
    forbidClick: true,
    duration: 0,
  });
  try {
    await pcCardAssetWithdraw(
      {
        address: profileStore.platformAddress || null,
        amount: amount.value,
        symbol: selectedCoin.value.symbol,
        symbolType: selectedChain.value.symbolType,
        addressTo: addressTo.value.trim(),
        code: emailCode.value.trim(),
        payPwd: pendingPayPassword.value,
      },
      { meta: { showLoading: false } },
    );
    closeToast();
    showWithdrawVerification.value = false;
    addressTo.value = "";
    amount.value = "";
    showPassedToast(t("wallet.withdrawSuccess"));
  } catch (error) {
    closeToast();
    showFailToast(error?.msg || t("common.retryLater"));
  } finally {
    isSubmitting.value = false;
  }
}

function goForgotPassword() {
  resetPasswordPopup();
  router.push("/auth/forgot-pay-password");
}

async function loadData() {
  showLoadingToast({
    message: t("common.loading"),
    forbidClick: true,
    duration: 0,
  });

  try {
    await withdrawStore.fetchWithdrawInfo({
      address: profileStore.platformAddress || null,
      reset: !withdrawStore.list.length,
    });
    selectedChainIndex.value = 0;
    showChainPopup.value = false;
  } catch (error) {
    showFailToast(error?.msg || t("common.retryLater"));
  } finally {
    closeToast();
  }
}

onMounted(loadData);

onBeforeUnmount(() => {
  window.clearInterval(emailCountdownTimer);
});
</script>

<style scoped lang="scss">
.wallet-withdraw-page {
  min-height: var(--app-viewport-height);
  padding: 15px 14px 34px;
  background: #f7f7f7;
}

.wallet-card {
  margin-bottom: 10px;
  padding: 13px 16px;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
}
.pt-20 {
  padding-bottom: 20px;
}

.network-card {
  position: relative;
  z-index: 3;
}

.selector-card,
.amount-title,
.detail-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.amount-title {
  span {
    font-weight: bold;
  }
}

.selector-card {
  span {
    display: flex;
    align-items: center;
  }
  &__arrow {
    width: 29px;
    height: 29px;
    margin-left: 4px;
    flex: 0 0 29px;
    background: var(--wp-color-theme);
    -webkit-mask: url("@/assets/images/img_account_right.png") center / contain
      no-repeat;
    mask: url("@/assets/images/img_account_right.png") center / contain
      no-repeat;
  }
}

.selector-card > span,
.amount-title span,
.detail-row span {
  color: rgba(0, 0, 0, 0.65);
  font-size: 14px;
}

.coin-title {
  display: flex;
  align-items: center;

  .app-image {
    width: 40px;
    height: 40px;
    margin-right: 8px;
    border-radius: 50%;
    object-fit: cover;
  }

  [data-strong] {
    color: #000000;
    font-size: 16px;
    font-weight: 700;
  }
}

h2 {
  margin: 0 0 14px;
  color: #000000;
  font-size: 16px;
  font-weight: 500;
}

.amount-title h2 {
  margin-bottom: 0;
}

.network-select,
.input-shell,
.amount-field {
  display: flex;
  align-items: center;
  min-height: 52px;
  padding: 0 12px 0 16px;
  border-radius: 8px;
  background: #f7f7f7;
}

.network-select {
  justify-content: space-between;
  padding: 0 16px;
  color: #1d2129;
  font-size: 14px;
  cursor: pointer;

  :deep(.van-icon) {
    color: #909399;
    font-size: 18px;
  }
}

.network-options {
  position: absolute;
  top: 94px;
  left: 16px;
  right: 16px;
  overflow: hidden;
  border-radius: 20px;
  background: #ffffff;
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.14);
  z-index: 5;

  &__item {
    width: 100%;
    padding: 10px 18px;
    border: 0;
    background: #ffffff;
    color: #1d2129;
    font-size: 14px;
    text-align: left;
  }

  &__item + &__item {
    border-top: 1px solid #f5f5f5;
  }

  &__item--active {
    background: #d9d9d9;
  }
}

.network-mask {
  position: fixed;
  inset: 0;
  z-index: 2;
  background: transparent;
}

.input-shell input,
.amount-field input {
  flex: 1;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: #000000;
  font-size: 14px;
  line-height: 20px;
}

.input-shell input::placeholder,
.amount-field input::placeholder {
  color: #999999;
}

.scan-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;

  .app-image {
    width: 25px;
    height: 25px;
    object-fit: contain;
  }
}

.amount-field {
  margin-top: 10px;
}

.amount-side {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 8px;
}

.amount-symbol {
  color: #000000;
  font-size: 16px;
  font-weight: 700;
}

.all-btn {
  padding: 8px 12px;
  border: 0;
  border-radius: 4px;
  background: var(--van-primary-color);
  color: #ffffff;
  font-size: 13px;
  line-height: 1;
}

.dashed-divider {
  width: 100%;
  height: 1px;
  margin: 15px 0;
  background-image: linear-gradient(
    to right,
    #dcdfe6 60%,
    rgba(255, 255, 255, 0) 0%
  );
  background-size: 8px 1px;
  background-repeat: repeat-x;
}

.detail-row {
  [data-strong] {
    color: #000000;
    font-size: 14px;
    font-weight: 700;
  }
}

.submit-btn {
  margin: 20px 0 30px;
}

.notes-card p {
  margin: 6px 0 0;
  color: #333333;
  font-size: 12px;
  line-height: 18px;

  [data-strong] {
    color: #000000;
    font-weight: 500;
  }
}

.empty-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 80px;

  .app-image {
    width: 100px;
    height: 100px;
    object-fit: contain;
  }

  span {
    margin-top: 12px;
    color: #333333;
    font-size: 14px;
  }
}

.withdraw-email-verification-popup {
  right: 0;
  left: 0;
  width: 100%;
  height: min(412px, 90vh);
  margin: 0 auto;
  overflow: hidden;
  border-radius: 20px 20px 0 0;
  background: #ffffff;
}

.withdraw-email-verification {
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  box-sizing: border-box;
  padding: 20px 16px 0;
  background: #ffffff;
}

.withdraw-email-verification__head {
  position: relative;
  display: flex;
  height: 22px;
  flex: 0 0 22px;
  align-items: center;
  justify-content: center;

  h2 {
    margin: 0;
    color: #000000;
    font-size: 20px;
    font-weight: 600;
    line-height: 22px;
  }

  button {
    position: absolute;
    top: 2px;
    right: 0;
    display: flex;
    width: 18.18px;
    height: 18.18px;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    background: transparent;
  }

  img {
    display: block;
    width: 18.18px;
    height: 18.18px;
  }
}

.withdraw-email-verification__desc {
  margin: 30px 0 26px;
  color: #000000;
  font-size: 16px;
  font-weight: 500;
  line-height: 26px;
  text-align: center;
}

.withdraw-email-verification__account {
  display: flex;
  height: 72px;
  flex: 0 0 72px;
  align-items: center;
  gap: 10px;
  box-sizing: border-box;
  padding: 14px 16px;
  border-radius: 16px;
  background: #f7f3ff;
}

.withdraw-email-verification__image {
  position: relative;
  display: block;
  width: 52px;
  height: 42px;
  flex: 0 0 52px;
  overflow: hidden;

  img {
    position: absolute;
    top: -16.67%;
    left: 0;
    display: block;
    width: 100%;
    height: 123.81%;
    object-fit: cover;
  }
}

.withdraw-email-verification__email {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 10px;

  strong {
    color: #090a1d;
    font-size: 16px;
    font-weight: 500;
    line-height: 16px;
  }

  span {
    overflow: hidden;
    color: #7442e8;
    font-size: 14px;
    line-height: 18px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.withdraw-email-verification__code {
  margin-top: 16px;

  label {
    display: block;
    color: #000000;
    font-size: 14px;
    line-height: 14px;
  }

  :deep(.van-field) {
    height: 42px;
    margin-top: 12px;
    padding: 8px 8px 8px 16px;
    border-radius: 10px;
    background: #f4f5f6;
  }

  :deep(.van-cell::after) {
    display: none;
  }

  :deep(.van-field__control) {
    color: #090a1d;
    font-size: 14px;
    line-height: 14px;
  }

  :deep(.van-field__control::placeholder) {
    color: #999999;
  }

  :deep(.van-field__button) {
    padding-left: 8px;
  }

  > p {
    margin: 10px 0 0;
    color: #999999;
    font-size: 12px;
    line-height: 12px;
    text-align: right;
  }
}

.withdraw-email-verification__send {
  display: inline-flex;
  min-width: 78px;
  height: 26px;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  border: 0;
  border-radius: 6px;
  background: #e7e5ff;
  color: #7442e8;
  font-size: 12px;
  white-space: nowrap;

  &:disabled {
    opacity: 0.7;
  }
}

.withdraw-email-verification__footer {
  display: flex;
  width: 100%;
  flex: 0 0 80px;
  flex-direction: column;
  align-items: center;
  margin-top: auto;

  :deep(.van-button) {
    height: 46px;
    border: 0;
    background: #7442e8;
    color: #ffffff;
    font-size: 16px;
    font-weight: 600;
  }

  :deep(.van-button--disabled) {
    color: #999999 !important;
    opacity: 1;
  }
}

.withdraw-email-verification__home-indicator {
  position: relative;
  display: block;
  width: 100%;
  height: 34px;
  flex: 0 0 34px;

  &::after {
    position: absolute;
    bottom: 8px;
    left: 50%;
    width: 135px;
    height: 5px;
    border-radius: 50px;
    background: #000000;
    content: "";
    transform: translateX(-50%);
  }
}

@media (max-height: 460px) {
  .withdraw-email-verification-popup {
    height: 90vh;
    overflow-y: auto;
  }

  .withdraw-email-verification {
    min-height: 412px;
  }
}
</style>
